import React, { RefObject } from 'react';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { UseQueryResult } from '@tanstack/react-query';
import { PaginationResults, PersonaProfileResponse, SwapResponse, UserResponse } from '../../api/types';
import ProfilePicker from '../../partials/profile-picker';
import { useGetPersonaProfiles } from '../../hooks/api-hooks';

interface UploadAreaProps {
  isDragging: boolean;
  isUploading: boolean;
  uploadProgress: number;
  selectedFiles: File[];
  handleDrop: (e: React.DragEvent<HTMLLabelElement>) => void;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleRemoveFile: (index: number) => void;
  handleSwapClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
  fileInputRef: RefObject<HTMLInputElement>;
  swapsQuery: UseQueryResult<PaginationResults<SwapResponse>, Error>;
  isAnyFilteredEnabled: boolean;
  user: UseQueryResult<UserResponse, Error>;
  MAX_UPLOADS: number;
  setIsDragging: (isDragging: boolean) => void;
  currentProfile: PersonaProfileResponse | undefined;
  setSelectedProfileId: (id: string) => void;
  selectedProfileId: string | undefined | null;
}

const UploadArea: React.FC<UploadAreaProps> = ({
   isDragging,
   isUploading,
   uploadProgress,
   selectedFiles,
   handleDrop,
   handleFileInputChange,
   handleRemoveFile,
   handleSwapClick,
   fileInputRef,
   user,
   MAX_UPLOADS,
   setIsDragging,
   currentProfile,
   setSelectedProfileId,
   selectedProfileId,
 }) => {

  const { data: profiles } = useGetPersonaProfiles();

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4 mt-5">
      <div className="relative">
        <label
          className={`relative block w-full rounded-lg border-2 border-dashed p-6 sm:p-12 text-center focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
            isDragging ? 'border-indigo-500' : 'border-gray-300 hover:border-gray-400'
          } ${selectedFiles.length > 0 ? 'mb-0' : 'mb-4'}`}
          onDragEnter={() => setIsDragging(true)}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <input
            type="file"
            accept="image/*, video/*"
            className="sr-only"
            onChange={handleFileInputChange}
            multiple
            ref={fileInputRef}
          />

        {/* Upload States */}
        <div className="space-y-4">
          {/* Empty State */}
          {selectedFiles.length === 0 && !isUploading && (
            <div className="space-y-2">
              <PhotoIcon className="mx-auto h-12 w-12 text-indigo-500" />
              <div>
                <p className="text-lg font-semibold">
                  <span className="text-indigo-500">Upload</span> images and videos
                </p>
                <p className="text-sm text-gray-500 mt-1">MP4, QTIME, JPG, PNG, WEBP</p>
              </div>
            </div>
          )}

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && !isUploading && (
              <div className="space-y-6">
                {/* File count + max */}
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">
                    {selectedFiles.length} {selectedFiles.length === 1 ? 'file' : 'files'} selected
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedFiles.length}/{MAX_UPLOADS} max
                  </p>
                </div>

                {/* Grid of selected files */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-4">
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
                    >
                      {file.type.startsWith('video') ? (
                        <video
                          src={URL.createObjectURL(file)}
                          controls={true}
                          playsInline
                          muted
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <img
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          className="h-full w-full object-cover"
                        />
                      )}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          handleRemoveFile(index);
                        }}
                        className="absolute top-1 right-1 rounded-full bg-gray-900/50 p-1 text-white hover:bg-gray-900/75"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-gray-900/50 px-2 py-1">
                        <p className="text-xs text-white truncate">
                          {formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Action Section (profile + anonymize button) */}
                <div className="pt-6 mt-6 border-t border-gray-200">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    {/* Profile block */}
                    <div className="flex items-center gap-4">
                      <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden flex-shrink-0">
                        {currentProfile?.url ? (
                          <img
                            src={currentProfile.url}
                            alt="Profile Face"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gray-300" />
                        )}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-gray-700">
                          Ready to anonymize with:
                        </span>
                        {/* If more than one profile, show the dropdown; otherwise just the name */}
                        {profiles && profiles.length > 1 ? (
                          <div className="mt-1">
                            <ProfilePicker
                              profiles={profiles}
                              selectedProfileId={selectedProfileId}
                              onChange={(id) => setSelectedProfileId(id)}
                            />
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-700">
                            {currentProfile?.displayName || 'Unnamed Profile'}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Anonymize button */}
                    <button
                      className="w-full sm:w-auto rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                      onClick={(e) => handleSwapClick(e)}
                      disabled={isUploading}
                    >
                      Anonymize {selectedFiles.length}{' '}
                      {selectedFiles.length === 1 ? 'file' : 'files'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* 3) Loading State */}
            {isUploading && (
              <div className="space-y-4">
                <div className="flex justify-center">
                  <svg
                    className="h-12 w-12 text-indigo-500 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0
                         12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135
                         5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
                <p className="text-lg font-semibold">Uploading...</p>
                <div className="w-full bg-gray-200 rounded-full h-2.5 max-w-md mx-auto">
                  <div
                    className="bg-indigo-500 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Persona Section when no files selected + not uploading */}
          {selectedFiles.length === 0 && !isUploading && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-center gap-4 sm:gap-6">
                {/* Profile image */}
                <div className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-full overflow-hidden">
                  {currentProfile?.url ? (
                    <img
                      src={currentProfile.url}
                      alt="Current Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-300" />
                  )}
                </div>

                {/* Profile details */}
                <div className="flex flex-col text-left">
                  <p className="text-sm font-semibold text-gray-700 mb-1">
                    Anonymize With
                  </p>
                  {profiles && profiles.length > 1 ? (
                    <div className="mb-1">
                      <ProfilePicker
                        profiles={profiles}
                        selectedProfileId={selectedProfileId}
                        onChange={(id) => setSelectedProfileId(id)}
                      />
                    </div>
                  ) : (
                    <p className="mb-1 text-sm text-gray-700">
                      {currentProfile?.displayName || 'Unnamed Profile'}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </label>
      </div>
    </div>
  );
};

export default UploadArea;
